<template>
  <TTabsToggle
    :header="$t('forms.settings_element_section')"
    no-move
    :changed="wasChanged"
    data-testid="section-settings"
  >
    <menu-content-container border-full :padding-left-right="8">
      <content-row class="!items-end">
        <color-picker
          data-testid="structure-color"
          :label="$t('forms.settings_block_bgc')"
          position="left"
          :default-color="blockColor.model"
          :changed="blockColor.changed"
          @color-change="blockColor.model = $event"
          @reset="blockColor.reset"
        ></color-picker>
      </content-row>
    </menu-content-container>

    <menu-content-container border-full :padding-left-right="8">
      <TLabel
        :changed="backgroundImage.changed || gradient.changed"
        :tooltip="$t('forms.reset_value')"
        @reset="onBackgroundReset"
        >{{ $t("forms.section_bgi") }}</TLabel
      >
      <content-row
        :margin-top="8"
        class="gap-y-2"
        style="justify-content: flex-start; flex-wrap: wrap"
      >
        <ImageButtons
          v-model="backgroundImage.model"
          :show-remove="!setDynamic"
          show-replace
          :class="{ 'mb-2': !setDynamic && backgroundImage.model }"
        />
        <TButton
          data-testid="image-set-dynamic"
          @click="setDynamic = !setDynamic"
        >
          <template #icon>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.4375 12.5625C15.7422 12.5625 16 12.8203 16 13.125C16 13.4531 15.7422 13.6875 15.4375 13.6875H7.65625C7.42188 14.4609 6.69531 15 5.875 15C4.82031 15 4 14.1797 4 13.125C4 12.0938 4.82031 11.25 5.875 11.25C6.69531 11.25 7.42188 11.8125 7.65625 12.5625H15.4375ZM5.125 13.125C5.125 13.5469 5.45312 13.875 5.875 13.875C6.27344 13.875 6.625 13.5469 6.625 13.125C6.625 12.7266 6.27344 12.375 5.875 12.375C5.45312 12.375 5.125 12.7266 5.125 13.125ZM12.3203 7.3125C12.5547 6.5625 13.2812 6 14.125 6C15.1562 6 16 6.84375 16 7.875C16 8.92969 15.1562 9.75 14.125 9.75C13.2812 9.75 12.5547 9.21094 12.3203 8.4375H4.5625C4.23438 8.4375 4 8.20312 4 7.875C4 7.57031 4.23438 7.3125 4.5625 7.3125H12.3203ZM14.125 8.625C14.5234 8.625 14.875 8.29688 14.875 7.875C14.875 7.47656 14.5234 7.125 14.125 7.125C13.7031 7.125 13.375 7.47656 13.375 7.875C13.375 8.29688 13.7031 8.625 14.125 8.625Z"
                fill="currentColor"
              />
            </svg>
          </template>
          {{ $t("forms.image_set_dynamic") }}
        </TButton>
        <TButton
          v-if="!gradient.model"
          data-testid="add-gradient"
          @click="addGradient"
        >
          <template #icon>
            <svg
              width="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.958 3C20.3669 3 20.7597 3.16205 21.0488 3.45117C21.3379 3.74029 21.5 4.13312 21.5 4.54199V19.958C21.5 20.3669 21.3379 20.7597 21.0488 21.0488C20.7597 21.3379 20.3669 21.5 19.958 21.5H4.54199C4.13312 21.5 3.74029 21.3379 3.45117 21.0488C3.16205 20.7597 3 20.3669 3 19.958V4.54199C3 4.13312 3.16205 3.74029 3.45117 3.45117C3.74029 3.16205 4.13312 3 4.54199 3H19.958ZM10.708 4.54199V7.625H7.625V10.708H10.708V13.792H7.625V16.875H10.708V19.958H13.792V16.875H16.875V19.958H19.958V4.54199H16.875V7.625H13.792V4.54199H10.708ZM13.792 16.875H10.708V13.792H13.792V16.875ZM16.875 13.792H13.792V10.708H16.875V13.792ZM13.792 10.708H10.708V7.625H13.792V10.708Z"
                fill="currentColor"
              />
            </svg>
          </template>
          {{ $t("forms.add_gradient") }}
        </TButton>
      </content-row>
      <content-row class="!block" data-testid="gradient-form">
        <TTabsToggle
          v-if="gradient.model"
          :header="$t('forms.gradient')"
          :changed="gradient.changed"
          default-open
          no-move
          show-remove
          can-be-reset
          @remove-block="gradient.model = undefined"
          @reset="gradient.reset"
        >
          <menu-content-container border-full :padding-left-right="8">
            <TGradient v-model="gradient.model" />
          </menu-content-container>
        </TTabsToggle>
      </content-row>

      <content-row v-if="setDynamic">
        <t-auto-complete
          v-model="backgroundImage.model"
          data-testid="image-dynamic-src"
          :label="`${$t('forms.image_url')} / ${$t('forms.dynamic_image')}`"
          placeholder="Some placeholder"
        ></t-auto-complete>
      </content-row>

      <content-row v-if="backgroundImage.model" justify-between>
        <TDropdown
          data-testid="section-size-options"
          :label="$t('forms.image_size_options_label')"
          :selected="
            backgroundImageSize.model
              ? $t('forms.image_size_options.' + backgroundImageSize.model)
              : undefined
          "
          rounded-full
          :data="[
            { value: 'auto', label: $t('forms.image_size_options.auto') },
            { value: 'cover', label: $t('forms.image_size_options.cover') },
            {
              value: 'contain',
              label: $t('forms.image_size_options.contain'),
            },
          ]"
          :changed="backgroundImageSize.changed"
          @selection-changed="
            (val: TDropdownOption) => (backgroundImageSize.model = val.value)
          "
          @reset="backgroundImageSize.reset"
        ></TDropdown>
        <t-switch
          v-model="backgroundRepeat"
          class="mt-6"
          data-testid="section-repeat-switch"
          :label="$t('forms.repeat_background')"
          :changed="backgroundRepeatBinding.changed"
          @reset="backgroundRepeatBinding.reset"
        ></t-switch>
      </content-row>

      <content-row :margin-bottom="16">
        <t-note>{{ $t("layout.background_image_text") }}</t-note>
      </content-row>
    </menu-content-container>

    <menu-content-container border-full>
      <TLabel :changed="fullWidth.changed" @reset="fullWidth.reset">{{
        $t("forms.structure_width")
      }}</TLabel>
      <content-row justify-between>
        <TButtonGroup>
          <TGroupButton
            data-testid="column-width"
            data="column"
            small
            :active="fullWidth.model !== 'full-width'"
            @button-clicked="fullWidth.model = false"
          >
            <ColumnWidth />
          </TGroupButton>
          <TGroupButton
            data-testid="full-width"
            data="full"
            small
            :active="fullWidth.model === 'full-width'"
            @button-clicked="fullWidth.model = 'full-width'"
          >
            <FullWidth />
          </TGroupButton>
        </TButtonGroup>

        <t-switch
          v-model="stuckOnMobile"
          data-testid="do-not-stack-on-mobile-switch"
          :label="$t('forms.stuck')"
          :changed="inGroup.changed"
          @reset="inGroup.reset"
        ></t-switch>
      </content-row>
    </menu-content-container>

    <padding-form
      external-usage
      :external-padding="padding.model"
      data-testid="settings-section-padding"
      :changed="padding.changed"
      :default-value="padding.defaultValue"
      @padding-changed="padding.model = $event"
      @reset="padding.reset"
    />

    <borderRadiusForm
      data-testid="image-border-radius"
      :label="$t('forms.border_radius')"
      external-usage
      :external-border-radius="borderRadius.model"
      :changed="borderRadius.changed"
      :default-value="borderRadius.defaultValue"
      @borderRadiusChanged="borderRadius.model = $event"
      @reset="borderRadius.reset"
    ></borderRadiusForm>
    <border-form
      data-testid="section-border-form"
      :header-title="$t('forms.structure_border')"
      :border="border.model"
      :default-border="border.defaultValue"
      :default-border-top="borderTop.defaultValue"
      :default-border-right="borderRight.defaultValue"
      :default-border-bottom="borderBottom.defaultValue"
      :default-border-left="borderLeft.defaultValue"
      :border-top="borderTop.model"
      :border-bottom="borderBottom.model"
      :border-left="borderLeft.model"
      :border-right="borderRight.model"
      :changed="borderAnyChanged"
      :changed-top="borderTop.changed"
      :changed-right="borderRight.changed"
      :changed-bottom="borderBottom.changed"
      :changed-left="borderLeft.changed"
      @borderChanged="onBorderChanged"
      @reset="onBorderReset"
      @reset-top="borderTop.reset"
      @reset-right="borderRight.reset"
      @reset-bottom="borderBottom.reset"
      @reset-left="borderLeft.reset"
    ></border-form>
  </TTabsToggle>
</template>

<script setup lang="ts">
  import paddingForm from "@/forms/paddingForm.vue";
  import borderRadiusForm from "@/forms/borderRadiusForm.vue";
  import borderForm from "@/forms/borderForm.vue";
  import colorPicker from "../../elements/colorPicker.vue";
  import TGradient from "@/components/TGradient/TGradient.vue";
  import ImageButtons from "@/forms/components/ImageButtons/ImageButtons.vue";
  import { useBlockAttr } from "@/composables/useBlockAttr";
  import {
    TTabsToggle,
    TSwitch,
    TButton,
    TLabel,
    TButtonGroup,
    TGroupButton,
    TNote,
    TDropdown,
    TMergetagsAutocomplete as TAutoComplete,
    type TDropdownOption,
  } from "@topol/twig-ui";
  import { ColumnWidth, FullWidth } from "@topol/icons";
  import { computed, ref, watch } from "vue";

  const { nullable, str, anyChanged, anyPropChanged, resetAttr } = useBlockAttr(
    {
      mode: "global",
      block: "mj-section",
    }
  );

  const setDynamic = ref(false);

  // --- Composable bindings ---
  const blockColor = nullable("background-color");
  const gradient = nullable("gradient");
  const padding = str("padding", "0px");
  const borderRadius = nullable("border-radius");
  const backgroundImageSize = str("background-size", "cover");
  const fullWidth = nullable("full-width");
  const inGroup = nullable("in-group");
  const backgroundImage = nullable("background-url");
  const backgroundRepeatBinding = nullable("background-repeat");
  const backgroundRepeat = computed({
    get() {
      return backgroundRepeatBinding.model !== "no-repeat";
    },
    set(val: boolean) {
      backgroundRepeatBinding.model = val ? "repeat" : "no-repeat";
    },
  });
  const border = nullable("border");
  const borderTop = nullable("border-top");
  const borderBottom = nullable("border-bottom");
  const borderLeft = nullable("border-left");
  const borderRight = nullable("border-right");
  const borderAnyChanged = anyPropChanged(
    "border",
    "border-top",
    "border-bottom",
    "border-left",
    "border-right"
  );

  // When background image is set, encode URI and clear gradient
  watch(
    () => backgroundImage.model,
    (val) => {
      if (!val) return;
      const isEncoded = decodeURI(val) !== val;
      if (!isEncoded) {
        backgroundImage.model = encodeURI(val);
      }
      if (gradient.model) {
        gradient.model = undefined;
      }
    }
  );

  // When gradient is set, clear background image
  watch(
    () => gradient.model,
    (val) => {
      if (val && backgroundImage.model) {
        backgroundImage.model = undefined;
      }
    }
  );

  const stuckOnMobile = computed({
    get() {
      return !inGroup.model;
    },
    set(val: boolean) {
      inGroup.model = !val;
    },
  });

  function onBorderChanged({
    border: newBorder,
    borderType = "border",
  }: {
    border: string;
    borderType?: string;
  }) {
    if (borderType !== "border" && border.model !== undefined) {
      border.model = undefined;
    }

    const value =
      newBorder === undefined || newBorder.startsWith("0px")
        ? undefined
        : newBorder;

    const mapping: Record<string, typeof border> = {
      border,
      "border-top": borderTop,
      "border-bottom": borderBottom,
      "border-left": borderLeft,
      "border-right": borderRight,
    };
    const target = mapping[borderType];
    if (target) target.model = value;
  }

  function onBackgroundReset() {
    backgroundImage.reset();
    gradient.reset();
  }

  function onBorderReset() {
    for (const prop of [
      "border",
      "border-top",
      "border-bottom",
      "border-left",
      "border-right",
    ]) {
      resetAttr(prop);
    }
  }

  function addGradient() {
    gradient.model = "linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)";
  }

  // --- Was Changed ---
  const wasChanged = computed(() => {
    return (
      anyChanged(
        blockColor,
        gradient,
        padding,
        borderRadius,
        backgroundImageSize,
        fullWidth,
        inGroup,
        backgroundImage,
        backgroundRepeatBinding
      ).value || borderAnyChanged.value
    );
  });
</script>
